import { useCallback, useMemo, useRef, useState } from "react";
import {
  activeTodoMentionQuery,
  listTodoMentionCandidates,
  type WaveParticipantRecord,
  type WaveTodoMentionCandidate,
} from "@vida/wave-core";
import { WaveTodoMentionPicker } from "../blocks/WaveTodoMentionPicker";

type Props = {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  placeholder: string;
  ariaLabel: string;
  mentionRoster: WaveParticipantRecord[];
  className?: string;
  inputClassName?: string;
  autoFocus?: boolean;
  disabled?: boolean;
};

export function WaveMentionComposer({
  value,
  onChange,
  onSubmit,
  placeholder,
  ariaLabel,
  mentionRoster,
  className = "comment-compose",
  inputClassName = "wave-title comment-compose-input",
  autoFocus = false,
  disabled = false,
}: Props) {
  const [mentionStart, setMentionStart] = useState<number | null>(null);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionIndex, setMentionIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);
  const skipBlurRef = useRef(false);

  const mentionOpen = mentionStart != null;
  const mentionCandidates = useMemo(
    () => listTodoMentionCandidates(mentionRoster, mentionQuery),
    [mentionRoster, mentionQuery],
  );

  const updateMentionState = useCallback((text: string, cursor: number) => {
    const active = activeTodoMentionQuery(text, cursor);
    if (active) {
      setMentionStart(active.start);
      setMentionQuery(active.query);
      setMentionIndex(0);
    } else {
      setMentionStart(null);
      setMentionQuery("");
      setMentionIndex(0);
    }
  }, []);

  const applyMention = useCallback(
    (candidate: WaveTodoMentionCandidate) => {
      const input = inputRef.current;
      const cursor = input?.selectionStart ?? value.length;
      const before = value.slice(0, mentionStart ?? cursor);
      const after = value.slice(cursor);
      const insert = `@${candidate.label} `;
      const next = before + insert + after;
      onChange(next);
      setMentionStart(null);
      setMentionQuery("");
      setMentionIndex(0);
      requestAnimationFrame(() => {
        const pos = before.length + insert.length;
        input?.setSelectionRange(pos, pos);
        input?.focus();
      });
    },
    [mentionStart, onChange, value],
  );

  const handleChange = (next: string) => {
    onChange(next);
    const cursor = inputRef.current?.selectionStart ?? next.length;
    updateMentionState(next, cursor);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (mentionOpen) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setMentionIndex((i) => Math.min(i + 1, Math.max(mentionCandidates.length - 1, 0)));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setMentionIndex((i) => Math.max(i - 1, 0));
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setMentionStart(null);
        return;
      }
      if (e.key === "Enter" && mentionCandidates.length > 0) {
        e.preventDefault();
        applyMention(mentionCandidates[mentionIndex] ?? mentionCandidates[0]!);
        return;
      }
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSubmit();
    }
  };

  return (
    <div className={className}>
      <div
        className="wave-todo-item-editor-wrap comment-mention-editor-wrap"
        onPointerDown={(e) => e.stopPropagation()}
      >
        <input
          ref={inputRef}
          type="text"
          className={inputClassName}
          value={value}
          placeholder={placeholder}
          aria-label={ariaLabel}
          disabled={disabled}
          autoFocus={autoFocus}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onClick={(e) => {
            const target = e.currentTarget;
            updateMentionState(target.value, target.selectionStart ?? target.value.length);
          }}
          onKeyUp={(e) => {
            const target = e.currentTarget;
            updateMentionState(target.value, target.selectionStart ?? target.value.length);
          }}
          onBlur={() => {
            if (skipBlurRef.current) {
              skipBlurRef.current = false;
              return;
            }
            window.setTimeout(() => {
              const active = document.activeElement;
              if (pickerRef.current?.contains(active)) return;
              if (inputRef.current === active) return;
              setMentionStart(null);
            }, 0);
          }}
        />
        {mentionOpen && !disabled && (
          <div
            ref={pickerRef}
            onMouseDown={() => {
              skipBlurRef.current = true;
            }}
          >
            <WaveTodoMentionPicker
              candidates={mentionCandidates}
              selectedIndex={mentionIndex}
              participants={mentionRoster}
              onSelect={applyMention}
              onHover={setMentionIndex}
            />
          </div>
        )}
      </div>
      <button
        type="button"
        className="primary-btn comment-send-btn"
        disabled={disabled || !value.trim()}
        onClick={onSubmit}
      >
        Invia
      </button>
    </div>
  );
}
